import type { RuntimeAnswer } from '../../types/runtime';

interface Props {
  answer: RuntimeAnswer;
}

function money(value: unknown): string {
  const n = typeof value === 'number' ? value : Number(value || 0);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function intValue(value: unknown): string {
  const n = typeof value === 'number' ? value : Number(value || 0);
  return Math.round(n).toLocaleString();
}

export function BillingSnapshotCard({ answer }: Props) {
  const summary = answer.summary || {};
  const riskFlags = answer.risk_flags || [];
  const isMultiMatter = answer.kind === 'billing_multi_matter_snapshot';
  return (
    <article className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-950">
      <div className="mb-3 flex items-center gap-2">
        <span className="rounded bg-emerald-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
          {isMultiMatter ? 'Billing overview' : 'Billing snapshot'}
        </span>
        {isMultiMatter && typeof answer.matter_count === 'number' && (
          <span className="text-xs text-emerald-800">{answer.matter_count} matters</span>
        )}
        {answer.matter_number && <span className="text-xs text-emerald-800">{answer.matter_number}</span>}
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2">
        <div className="rounded border border-emerald-200 bg-white/70 p-2">
          <div className="text-[11px] uppercase text-emerald-700">
            {isMultiMatter ? 'Draft / Prebill' : 'Unbilled WIP'}
          </div>
          <div className="text-base font-semibold">
            {money(isMultiMatter ? summary.prebill_total : summary.unbilled_wip_total)}
          </div>
        </div>
        <div className="rounded border border-emerald-200 bg-white/70 p-2">
          <div className="text-[11px] uppercase text-emerald-700">
            {isMultiMatter ? 'AR Exposure' : 'Draft / Prebill'}
          </div>
          <div className="text-base font-semibold">
            {money(isMultiMatter ? summary.ar_balance : summary.prebill_total)}
          </div>
        </div>
        <div className="rounded border border-emerald-200 bg-white/70 p-2">
          <div className="text-[11px] uppercase text-emerald-700">
            {isMultiMatter ? 'Overdue AR' : 'AR Exposure'}
          </div>
          <div className="text-base font-semibold">
            {money(isMultiMatter ? summary.overdue_balance : summary.ar_balance)}
          </div>
        </div>
        <div className="rounded border border-emerald-200 bg-white/70 p-2">
          <div className="text-[11px] uppercase text-emerald-700">
            {isMultiMatter ? 'Recent Invoices' : 'Print / Copy'}
          </div>
          {isMultiMatter ? (
            <div className="text-base font-semibold">{intValue(summary.recent_invoice_count)}</div>
          ) : (
            <>
              <div className="text-base font-semibold">{money(summary.print_copy_amount)}</div>
              <div className="text-[11px] text-emerald-700">{intValue(summary.print_copy_pages)} pages</div>
            </>
          )}
        </div>
      </div>

      {riskFlags.length > 0 && (
        <div className="mb-3 rounded border border-amber-200 bg-amber-50 p-2 text-amber-950">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide">Risk flags</div>
          <ul className="space-y-1">
            {riskFlags.slice(0, 5).map((flag, idx) => (
              <li key={`${String(flag.kind || idx)}`} className="flex justify-between gap-2">
                <span>{String(flag.label || flag.kind || 'Billing risk')}</span>
                {'count' in flag && <span>{String(flag.count)}</span>}
                {'amount' in flag && <span>{money(flag.amount)}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="whitespace-pre-line leading-relaxed">{answer.content}</p>
    </article>
  );
}
